import clsx from 'clsx';
import { forwardRef } from 'react';

import { FocusScope } from '../../core/components/focus-scope/FocusScope.js';
import { OverlayContainer } from '../../core/components/OverlayContainer.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { OverlayStackingProvider } from '../../core/providers/OverlayStackingProvider.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import type { OverlayProps } from '../hooks/useOverlayWithTrigger.js';
import { overlayCSS } from './Overlay.sty.js';

const COMPONENT_NAME = 'Overlay';

/**
 * @public
 */
export interface OverlayComponentProps
  extends
    AriaLabelingProps,
    WithChildren,
    DataTestId,
    StylingProps,
    MaskingProps,
    BehaviorTrackingProps {
  /** Whether the overlay is open. */
  isOpen: OverlayProps['overlayState']['isOpen'];
  /**
   * Properties for connecting the overlay to the overlay trigger.
   * Includes styles and accessibility attributes.
   */
  overlayProps?: OverlayProps['overlayProps'];
  /**
   * Properties to pass to the overlay container.
   */
  overlayContainerProps?: OverlayProps['overlayContainerProps'];
}

/**
 * The `Overlay` component allows you to show additional content when the user
 * clicks on a particular element in the UI. The `Overlay` is placed
 * at the border of its trigger element. To define a UI element as
 * a trigger, use the `useOverlayWithTrigger` hook. The Overlay only
 * exists in a controlled manner, so the `isOpen` prop must be
 * updated by the developer.
 * @public
 */
export const Overlay = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  OverlayComponentProps
>((props, forwardedRef) => {
  const {
    isOpen,
    children,
    overlayProps,
    overlayContainerProps,
    className: customClassName,
    style: customStyles,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    // @ts-expect-error - We want to allow overriding data-dt-component as we use the component internally in other Strato components, but it should not appear in the public API.
    'data-dt-component': dataDtComponent = COMPONENT_NAME,
    ...remainingProps
  } = props;
  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const {
    'data-dt-name': dataDtName = COMPONENT_NAME,
    ...remainingBehavioralTrackingProps
  } = behavioralTrackingProps;
  const ariaProps = useAriaLabelingProps(restProps);

  const defaultOverlayContainerProps = useOverlayStacking(
    undefined,
    'dropdown',
  );
  const overlayContainerPropsApplied = _mergeProps(
    defaultOverlayContainerProps,
    overlayContainerProps,
  );
  const { trapFocus, ...remainingOverlayProps } = overlayProps ?? {};

  if (!isOpen) {
    return null;
  }

  return (
    <OverlayContainer
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-dt-name={dataDtName}
      {...remainingBehavioralTrackingProps}
      {...overlayContainerPropsApplied}
      data-dt-component={dataDtComponent}
    >
      <FocusScope contain={trapFocus} autoFocus>
        <div
          ref={forwardedRef}
          {..._mergeProps(remainingOverlayProps, ariaProps)}
          className={clsx(
            overlayCSS,
            remainingOverlayProps.className,
            customClassName,
          )}
          style={{
            ...remainingOverlayProps.style,
            ...customStyles,
          }}
        >
          <OverlayStackingProvider>{children}</OverlayStackingProvider>
        </div>
      </FocusScope>
    </OverlayContainer>
  );
});

(Overlay as typeof Overlay & { displayName: string }).displayName =
  COMPONENT_NAME;
